import React from 'react';
import { Trash2Icon } from 'lucide-react';
import { nanoid } from 'nanoid';

import { ServiceQuestion } from '../api/get-question';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

type ChoicesEditorProps = {
  choices: ServiceQuestion['choices'];
  onChoicesChange: (choices: ServiceQuestion['choices']) => void;
};

export const QuestionChoicesEditor = ({
  choices,
  onChoicesChange,
}: ChoicesEditorProps) => {
  const [newChoice, setNewChoice] = React.useState('');

  const handleChoiceChange = (id: string, newValue: string) => {
    const newChoices = choices.map((choice) =>
      choice.id === id ? { ...choice, choice_text: newValue } : choice,
    );
    onChoicesChange(newChoices);
  };

  const handleDeleteChoice = (id: string) => {
    const newChoices = choices.filter((choice) => choice.id !== id);
    onChoicesChange(newChoices);
  };

  const handleNewChoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewChoice(e.target.value);
  };

  const handleNewChoiceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.key === 'Enter') {
      onChoicesChange([
        ...choices,
        {
          id: nanoid(),
          question_id: null,
          choice_text: newChoice,
        },
      ]);
      setNewChoice('');
    } else if (e.key === 'Escape') {
      setNewChoice('');
    }
  };

  return (
    <ScrollArea className="h-40">
      {choices.map((choice, idx) => (
        <div key={choice.id} className="flex items-center gap-2 p-2">
          <span className="text-gray-500">{idx + 1}</span>
          <Input
            value={choice.choice_text}
            onChange={(e) => handleChoiceChange(choice.id, e.target.value)}
          />
          <Button
            variant={'ghost'}
            size={'icon'}
            onClick={() => handleDeleteChoice(choice.id)}
          >
            <Trash2Icon className="w-3.5 h-3.5 text-red-500" />
          </Button>
        </div>
      ))}

      <Input
        type="text"
        placeholder="Ajouter un choix"
        className="w-full border-dashed border-2 border-gray-300 mt-2"
        value={newChoice}
        onChange={handleNewChoiceChange}
        onKeyDown={handleNewChoiceKeyDown}
      />
    </ScrollArea>
  );
};
